import { inject, Injectable } from "@angular/core";
import { BaseService } from "./base.service";
import { catchError, map, Observable } from "rxjs";
import { HttpClient, HttpParams } from "@angular/common/http";
import { Helpers } from "src/helpers/helpers";
import { environment } from "src/environments/environment";
import { CareplusAgencyDashboard } from "src/models/careplus-agency-dashboard";
import { CareplusNewCustomer } from "src/models/careplus-new-customer";
import { Router } from "@angular/router";
import { AccountService } from "./account.service";
import { MatLegacySnackBar as MatSnackBar } from "@angular/material/legacy-snack-bar";
import { ICountService } from "./icount.service";
import { CareplusBatchAmount } from "src/models/careplus-batch-amount";
import { CareplusInitiateTxResponse } from "src/models/careplus-initiate-tx-response";
import { CareplusInitiateTxRequest } from "src/models/careplus-initiate-tx-request";
import { CareplusCompleteTxRequest } from "src/models/careplus-complete-tx-request";
import {
  CareplusPaymentRequest,
  CareplusVerifyPaymentReq,
} from "src/models/careplus-payment-requests";
import { PaymentLog } from "src/models/careplus-payment-log";

@Injectable({
  providedIn: "root",
})
export class CareplusAgencyService
  extends BaseService
  implements ICountService
{
  private pathAPI = environment.apiUrl;
  private _accountService = inject(AccountService);
  private _router = inject(Router);
  private _snackBar = inject(MatSnackBar);

  constructor(
    private _http: HttpClient,
    helper: Helpers,
  ) {
    super(helper);
    const userType = this.helper.getDecodedAccessToken(
      window.localStorage.token,
    ).type;
    if (
      userType.indexOf("LEVEL 0") === 0 ||
      userType.indexOf("LEVEL 1") === 0 ||
      userType.indexOf("LEVEL 2") === 0
    ) {
      this._accountService.getAccount().subscribe((data) => {
        if (!data.activated) {
          this.helper.logout();
          this._router.navigate(["/"]);
          this._snackBar.open(
            "Your account is not active, please contact admin.",
            "Close",
            {
              duration: 3000,
            },
          );
        }
      });
    }
  }

  sendCareplusLink(body: any, lang?: string): any {
    return this._http
      .post<any>(
        this.pathAPI + "Account/SendCarePlusPolicyRenewalSMS",
        body,
        super.header(null, lang),
      )
      .pipe(catchError(super.handleError));
  }

  getData(body: any): any {
    const { status, ...restBody } = body ?? {};

    const url = status
      ? `${this.pathAPI}Account/GetRenewCarePlusCustomers?Status=${encodeURIComponent(status)}`
      : `${this.pathAPI}Account/GetRenewCarePlusCustomers`;

    const headers = super
      .header()
      .headers.set("apikey", "a")
      .set("apiPassword", "b");

    return this._http
      .post<any>(url, restBody, { headers })
      .pipe(catchError(super.handleError));
  }
  getCount(body: any): any {
    const { status, ...restBody } = body ?? {};
    const url = status
      ? `${this.pathAPI}Account/GetRenewCarePlusCustomersCount?Status=${encodeURIComponent(status)}`
      : `${this.pathAPI}Account/GetRenewCarePlusCustomersCount`;
    return this._http
      .post<any>(url, restBody, super.header())
      .pipe(catchError(super.handleError));
  }

  get(id: number): any {
    return this._http
      .get<CareplusNewCustomer>(
        this.pathAPI + "Account/GetRenewCarePlusCustomersDetail/" + id,
        super.header(),
      )
      .pipe(catchError(super.handleError));
  }

  update(id: number, customer: any): any {
    return this._http
      .put<any>(
        this.pathAPI + "careplus/agency/renew-customer/" + id,
        customer,
        super.header(),
      )
      .pipe(catchError(super.handleError));
  }

  activateDeactivate(id: number, active: boolean) {
    return this._http
      .put<any>(
        this.pathAPI + "Account/GetRenewCarePlusCustomer/" + id + "/active",
        { active },
        super.header(),
      )
      .pipe(catchError(super.handleError));
  }

  getCareplusDashboardCounts(): Observable<CareplusAgencyDashboard> {
    return this._http
      .get<CareplusAgencyDashboard>(
        this.pathAPI + "agency/dashboard/careplus",
        this.agencyheader(),
      )
      .pipe(catchError(super.handleError));
  }

  getTotalAmount(ids: number[] = []): Observable<CareplusBatchAmount> {
    return this._http
      .post<CareplusBatchAmount>(
        this.pathAPI + "careplus/agency/batch/calculate",
        { ids },
        this.agencyheader(),
      )
      .pipe(catchError(super.handleError));
  }

  recalculateBatchAmount(id: number): Observable<CareplusBatchAmount> {
    return this._http
      .get<CareplusBatchAmount>(
        this.pathAPI + "careplus/agency/batch/" + id + "/recalculate",

        this.agencyheader(),
      )
      .pipe(catchError(super.handleError));
  }

  cancelBatchPayment(id: number): Observable<any> {
    return this._http
      .put(
        this.pathAPI + "careplus/agency/batch/" + id + "/cancel",
        {},
        this.agencyheader(),
      )
      .pipe(catchError(super.handleError));
  }

  initiateTx(
    id: number,
    body: CareplusInitiateTxRequest,
  ): Observable<CareplusInitiateTxResponse> {
    return this._http
      .post<CareplusInitiateTxResponse>(
        this.pathAPI + "careplus/agency/batch/" + id + "/initiate-payment",
        body,
        this.agencyheader(),
      )
      .pipe(catchError(super.handleError));
  }

  completeTx(id: number, body: CareplusCompleteTxRequest): Observable<any> {
    return this._http
      .post(
        this.pathAPI + "careplus/agency/batch/" + id + "/complete-payment",
        body,
        this.agencyheader(),
      )
      .pipe(catchError(super.handleError));
  }

  getPendingRequests(body: any): Observable<CareplusPaymentRequest[]> {
    return this._http
      .post<
        CareplusPaymentRequest[]
      >(this.pathAPI + "careplus/agency/payment-requests", body, this.agencyheader())
      .pipe(catchError(super.handleError));
  }

  verifyPendingRequest(id: number, body: CareplusVerifyPaymentReq) {
    return this._http
      .post(
        this.pathAPI + "careplus/agency/batch/" + id + "/verify",
        body,
        this.agencyheader(),
      )
      .pipe(catchError(super.handleError));
  }

  getBatchDetail(id: number): Observable<CareplusBatchAmount> {
    return this._http
      .get<CareplusBatchAmount>(
        this.pathAPI + "careplus/agency/batch/" + id,
        this.agencyheader(),
      )
      .pipe(catchError(super.handleError));
  }

  deleteImportedEmployee(id: number) {
    return this._http
      .delete(
        this.pathAPI + "Account/DeleteRenewCarePlusCustomer/" + id,
        this.agencyheader(),
      )
      .pipe(catchError(super.handleError));
  }

  getPayemntLog(paymentMode: string): Observable<PaymentLog[]> {
    const baseHeaders = this.agencyheader().headers;
    const headers = baseHeaders.set("accept-language", "en");

    return this._http
      .get<PaymentLog[]>(this.pathAPI + "careplus/agency/batch/payments", {
        headers,
        params: { paymentMode },
      })
      .pipe(catchError(super.handleError));
  }

  validateMember(data: {
    civilId?: string;
    passportNumber?: string;
  }): Observable<boolean> {
    return this._http
      .post<{
        isValid: boolean;
      }>(
        this.pathAPI + "careplus/policy/validate/member",
        data,
        this.agencyheader(),
      )
      .pipe(
        map((response) => response.isValid),
        catchError(super.handleError),
      );
  }

  protected agencyheader() {
    return { headers: super.header().headers.append("accept-language", "en") };
  }
}
